import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { MessageService } from 'primeng/api';
import { filter, first, map } from 'rxjs';

import { CustomerContextService } from '../customer-context/customer-context.service';
import { SessionState } from '../services/session.models';
import { SessionService } from '../services/session.service';
import { translatedObjectSignal } from '../utils/translated-object-signal';
import { CustomerAccessText, customerAccessTextKey } from './customer-access.translations';

export const customerAccessGuard: CanActivateFn = (route) => {
    const customerContext = inject(CustomerContextService);
    const session = inject(SessionService);
    const messageService = inject(MessageService);
    const router = inject(Router);
    const text = translatedObjectSignal<CustomerAccessText>(customerAccessTextKey);
    const customerNumber = route.paramMap.get('customerNumber') ?? '';

    const resolve = (state: SessionState): true | UrlTree => {
        if (customerContext.hasAccess(customerNumber)) {
            return true;
        }

        if (state !== SessionState.CustomerAccessError) {
            messageService.add({
                severity: 'error',
                summary: text().UnavailableCustomerSummary,
                detail: text().UnavailableCustomerDetail,
                life: 10_000,
            });
        }

        return router.createUrlTree(['/'], {
            queryParams: {
                error: state === SessionState.CustomerAccessError ? 'CUSTOMER_ACCESS_FAILED' : 'CUSTOMER_UNAVAILABLE',
                requestedCustomer: customerNumber || undefined,
            },
        });
    };

    return isCustomerAccessPending(session.state())
        ? toObservable(session.state).pipe(
              filter((state) => !isCustomerAccessPending(state)),
              first(),
              map(resolve),
          )
        : resolve(session.state());
};

export const preferredCustomerGuard: CanActivateFn = () => {
    const customerContext = inject(CustomerContextService);
    const session = inject(SessionService);
    const router = inject(Router);

    const resolve = (state: SessionState): true | UrlTree => {
        if (state === SessionState.CustomerAccessError) {
            return true;
        }

        const customerNumber = customerContext.preferredCustomerNumber();
        return customerNumber ? router.createUrlTree(['/', customerNumber, 'home']) : true;
    };

    return isCustomerAccessPending(session.state())
        ? toObservable(session.state).pipe(
              filter((state) => !isCustomerAccessPending(state)),
              first(),
              map(resolve),
          )
        : resolve(session.state());
};

function isCustomerAccessPending(state: SessionState): boolean {
    return state === SessionState.LoadingAuthentication || state === SessionState.LoadingCustomerAccess;
}
